import { useEffect } from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { Button, Result, Typography } from 'antd';

// 动态 import（路由级 React.lazy chunk）加载失败的识别。
// 高发场景：重新部署后用户仍停留在旧 index.html，旧 chunk 文件名（hash）已不存在 → fetch 404。
function isChunkLoadError(err: unknown): boolean {
  const msg = (err as { message?: string } | null)?.message ?? String(err ?? '');
  return /dynamically imported module|module script failed|ChunkLoadError|Failed to fetch dynamically|error loading dynamically imported/i.test(msg);
}

/**
 * 路由级根错误兜底（createBrowserRouter 的 errorElement）。
 * 取代 React Router 默认那句裸 "Unexpected Application Error!"：
 *  - chunk 加载失败 → 自动刷新一次拉取新版本（30s 内只刷一次，防死循环）
 *  - 其余渲染错误 → 可读 UI + 刷新/返回首页；dev 下附错误详情
 */
export default function RouteError() {
  const error = useRouteError() as { message?: string } | null;
  const navigate = useNavigate();
  const chunkErr = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkErr) return;
    const KEY = '__chunk_reload_ts';
    const last = Number(sessionStorage.getItem(KEY) || '0');
    if (Date.now() - last > 30_000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
    }
  }, [chunkErr]);

  if (chunkErr) {
    return (
      <Result
        status="info"
        title="正在更新到最新版本…"
        subTitle="检测到页面资源已更新，正在自动刷新。若未自动跳转，请点击下方按钮。"
        extra={<Button type="primary" onClick={() => window.location.reload()}>立即刷新</Button>}
      />
    );
  }

  return (
    <Result
      status="error"
      title="页面出错了"
      subTitle="该页面加载时发生异常，可尝试刷新或返回首页。若反复出现请联系技术支持。"
      extra={[
        <Button type="primary" key="reload" onClick={() => window.location.reload()}>刷新页面</Button>,
        <Button key="home" onClick={() => navigate('/')}>返回首页</Button>,
      ]}
    >
      {import.meta.env.DEV && error?.message && (
        <Typography.Paragraph type="secondary" style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
          {error.message}
        </Typography.Paragraph>
      )}
    </Result>
  );
}
